"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Header/HeaderScoreChip";

import { useCallback } from "react";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useFitnessScore } from "@/src/hooks/fitness";
import { ROUTER_PATH } from "@config/router";
import { trackEvent } from "@utils/tracking";
import { FitnessRadarChart } from "@components/fitness/RadarChart/FitnessRadarChart";

const I18n = I18nUtil.createScoped(Translations);

export function HeaderScoreChip() {
  const router = useShellRouter();
  const { data: fitnessScore } = useFitnessScore();

  const handleClick = useCallback(() => {
    if (!fitnessScore) {
      router.push(ROUTER_PATH.ONBOARDING);
      return;
    }
    trackEvent("ff_header_chip_click", {
      total: fitnessScore.value,
    });
    router.push(ROUTER_PATH.MY_SCORE);
  }, [fitnessScore, router]);

  // onboarding 未完成（无 score）：雷达图(0值) + "Get your score"
  if (!fitnessScore) {
    return (
      <button
        type="button"
        className="flex h-10 shrink-0 items-center gap-[6px] overflow-clip rounded-full bg-background_solid_neutral_primary pl-[7px] pr-[13px] py-[7px] shadow-[0px_0px_32px_0px_rgba(0,0,0,0.08)]"
        onClick={handleClick}
        aria-label="Get your score"
        data-testid="header-score-chip"
        data-openreplay-label="misc_maintab_scorechip"
        data-tracking-eventname="ff_header_chip_click"
      >
        <FitnessRadarChart
          total={0}
          control={0}
          safety={0}
          growth={0}
          size="chip"
          animate={false}
        />
        <span className="button_s whitespace-nowrap text-content_brand_primary">
          {I18n.t("Get your score")}
        </span>
      </button>
    );
  }

  // onboarding 完成：雷达图(score) + 分数（现有逻辑）
  const dimMap = new Map(fitnessScore.dimensions.map((d) => [d.code, d]));

  return (
    <button
      type="button"
      className="flex h-10 shrink-0 items-center gap-[6px] overflow-clip rounded-full bg-background_solid_neutral_primary pl-[7px] pr-[13px] py-[7px] shadow-[0px_0px_32px_0px_rgba(0,0,0,0.08)]"
      onClick={handleClick}
      aria-label={`Financial fitness score: ${fitnessScore.value}/100`}
      data-testid="header-score-chip"
      data-openreplay-label="misc_maintab_scorechip"
      data-tracking-eventname="ff_header_chip_click"
    >
      <FitnessRadarChart
        total={fitnessScore.value}
        control={dimMap.get("control")?.value ?? 0}
        safety={dimMap.get("safety")?.value ?? 0}
        growth={dimMap.get("growth")?.value ?? 0}
        size="chip"
        animate={false}
      />
      <span className="flex items-center whitespace-nowrap">
        <span className="body_bold_l text-content_primary">
          {fitnessScore.value}
        </span>
        <span className="body_s text-content_quaternary">/100</span>
      </span>
    </button>
  );
}
